import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import FullPlayerModal, { hapt } from './FullPlayerModal';
import { usePlayer } from '../contexts/PlayerContext';

/**
 * MobileBottomNav — P0/P1 PWA native chrome.
 *
 * Renders a fixed bottom tab bar (Home / Library / Studio / Profile) plus an
 * optional persistent mini-player above it. Visibility is gated to mobile
 * viewports (≤768px) via CSS in App.css so desktop layout is untouched.
 *
 * P1 update: tapping the mini-player no longer immediately navigates; instead
 * it opens `FullPlayerModal` via a Framer Motion shared-layout spring
 * (layoutId="vs-player-card") for an Apple-Music-style expand transition.
 *
 * The mini-player reads "now playing" hand-off from sessionStorage
 * (`vs_now_playing` JSON: { bookId, title, chapter, cover }). The
 * EnhancedPlayerPage writes that handoff on play.
 */
const STORAGE_KEY = 'vs_now_playing';

const TabIcon = ({ name }) => {
  const common = { width: 22, height: 22, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 1.75, strokeLinecap: 'round', strokeLinejoin: 'round' };
  switch (name) {
    case 'player':
      // Headphones — distinguishes the active listening view from the
      // Library shelf.
      return (
        <svg {...common}>
          <path d="M3 18v-6a9 9 0 0 1 18 0v6" />
          <path d="M21 19a2 2 0 0 1-2 2h-1v-7h3z" />
          <path d="M3 19a2 2 0 0 0 2 2h1v-7H3z" />
        </svg>
      );
    case 'library':
      return (
        <svg {...common}><rect x="4" y="3" width="4" height="18" rx="1" /><rect x="10" y="3" width="4" height="18" rx="1" /><path d="m16.5 5.5 3.7 16.4-1.95.4L14.55 6" /></svg>
      );
    case 'bookstore':
      // Storefront — bookstore icon (door + awning).
      return (
        <svg {...common}>
          <path d="M3 9h18l-1.5-4.5A2 2 0 0 0 17.6 3H6.4a2 2 0 0 0-1.9 1.5L3 9z" />
          <path d="M4 9v11a1 1 0 0 0 1 1h14a1 1 0 0 0 1-1V9" />
          <path d="M10 21v-7h4v7" />
        </svg>
      );
    case 'studio':
      return (
        <svg {...common}><rect x="9" y="2" width="6" height="12" rx="3" /><path d="M5 10a7 7 0 0 0 14 0" /><path d="M12 17v4" /><path d="M8 21h8" /></svg>
      );
    case 'profile':
      return (
        <svg {...common}><circle cx="12" cy="8" r="4" /><path d="M4 21a8 8 0 0 1 16 0" /></svg>
      );
    default:
      return null;
  }
};

const TABS = [
  { id: 'player', label: 'Player', route: '/player', matches: ['/player', '/'] },
  { id: 'library', label: 'Library', route: '/library', matches: ['/library'] },
  { id: 'bookstore', label: 'Bookstore', route: '/emporium', matches: ['/emporium', '/bookstore'] },
  { id: 'studio', label: 'Studio', route: '/creator-studio', matches: ['/creator-studio', '/creator-dashboard'] },
  { id: 'profile', label: 'Profile', route: '/account', matches: ['/account', '/login', '/auth'] },
];

const MobileBottomNav = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const player = usePlayer();
  const [nowPlaying, setNowPlaying] = useState(null);
  const [expanded, setExpanded] = useState(false);

  // Prefer the global PlayerProvider's nowPlaying (richer data: chapters,
  // author). Fall back to sessionStorage for any legacy consumer that wrote
  // the key directly before the provider was installed.
  const readNowPlaying = useCallback(() => {
    if (player.nowPlaying && player.nowPlaying.bookId) {
      setNowPlaying(player.nowPlaying);
      return;
    }
    try {
      const raw = sessionStorage.getItem(STORAGE_KEY);
      setNowPlaying(raw ? JSON.parse(raw) : null);
    } catch (_) {
      setNowPlaying(null);
    }
  }, [player.nowPlaying]);

  useEffect(() => {
    readNowPlaying();
    const handler = () => readNowPlaying();
    window.addEventListener('storage', handler);
    window.addEventListener('vs-now-playing-changed', handler);
    return () => {
      window.removeEventListener('storage', handler);
      window.removeEventListener('vs-now-playing-changed', handler);
    };
  }, [readNowPlaying]);

  // Add a body-level class so we can pad bottom of every page on mobile.
  useEffect(() => {
    document.body.classList.add('vs-has-mobile-chrome');
    return () => document.body.classList.remove('vs-has-mobile-chrome');
  }, []);

  useEffect(() => {
    if (nowPlaying && nowPlaying.bookId) {
      document.body.classList.add('vs-has-mini-player');
    } else {
      document.body.classList.remove('vs-has-mini-player');
      setExpanded(false); // close modal if track cleared
    }
  }, [nowPlaying]);

  // Lock body scroll while the full-player modal is open.
  useEffect(() => {
    if (expanded) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      return () => { document.body.style.overflow = prev; };
    }
    return undefined;
  }, [expanded]);

  const isActive = (tab) => {
    const path = location.pathname;
    if (tab.id === 'player') return path === '/' || path === '' || path.startsWith('/player');
    return tab.matches.some((m) => m !== '/' && (path === m || path.startsWith(`${m}/`)));
  };

  const handleTab = (tab) => {
    hapt(15);
    // Player tab: if we have a nowPlaying book, jump straight back into it
    // so users get a one-tap resume; otherwise the player page shows its
    // "Select a Book" empty state.
    if (tab.id === 'player' && nowPlaying && nowPlaying.bookId) {
      navigate(`/player?bookId=${encodeURIComponent(nowPlaying.bookId)}`);
      return;
    }
    navigate(tab.route);
  };

  const handleOpenExpanded = () => {
    if (!nowPlaying || !nowPlaying.bookId) return;
    hapt(20);
    setExpanded(true);
  };

  const handleDismiss = (e) => {
    e.stopPropagation();
    try { sessionStorage.removeItem(STORAGE_KEY); } catch (_) { /* noop */ }
    window.dispatchEvent(new Event('vs-now-playing-changed'));
  };

  return (
    <>
      <AnimatePresence>
        {expanded && nowPlaying && nowPlaying.bookId && (
          <FullPlayerModal nowPlaying={nowPlaying} onClose={() => setExpanded(false)} />
        )}
      </AnimatePresence>

      {nowPlaying && nowPlaying.bookId && !expanded && (
        <motion.div
          layoutId="vs-player-card"
          className="vs-mobile-mini-player"
          data-testid="mobile-mini-player"
          onClick={handleOpenExpanded}
          role="button"
          tabIndex={0}
          transition={{ type: 'spring', damping: 28, stiffness: 320 }}
        >
          <motion.div layoutId="vs-player-cover" className="vs-mp-thumb-wrap">
            {nowPlaying.cover ? (
              <img className="vs-mp-thumb" src={nowPlaying.cover} alt="" />
            ) : (
              <div className="vs-mp-thumb" aria-hidden="true" />
            )}
          </motion.div>
          <div className="vs-mp-meta">
            <div className="vs-mp-title" data-testid="mini-player-title">
              {nowPlaying.title || 'Resume listening'}
            </div>
            <div className="vs-mp-sub" data-testid="mini-player-sub">
              {nowPlaying.chapter || 'Tap to continue'}
            </div>
          </div>
          <button
            type="button"
            className="vs-mp-resume"
            data-testid="mini-player-resume-button"
            onClick={(e) => { e.stopPropagation(); hapt(15); navigate(`/player?bookId=${encodeURIComponent(nowPlaying.bookId)}`); }}
          >
            Resume
          </button>
          <button
            type="button"
            className="vs-mp-close"
            data-testid="mini-player-close-button"
            aria-label="Dismiss mini player"
            onClick={handleDismiss}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M18 6 6 18" /><path d="m6 6 12 12" />
            </svg>
          </button>
        </motion.div>
      )}

      <nav className="vs-mobile-bottom-nav" data-testid="mobile-bottom-nav" aria-label="Primary mobile navigation">
        {TABS.map((tab) => {
          const active = isActive(tab);
          return (
            <button
              key={tab.id}
              type="button"
              className={`vs-tab ${active ? 'vs-tab-active' : ''}`}
              data-testid={`mobile-bottom-tab-${tab.id}`}
              aria-current={active ? 'page' : undefined}
              onClick={() => handleTab(tab)}
            >
              <TabIcon name={tab.id} />
              <span>{tab.label}</span>
            </button>
          );
        })}
      </nav>
    </>
  );
};

export default MobileBottomNav;
